type DomainConstant = string | string[];
type ContextType = Record<string, string>;

export interface DomainsConfig<
  SuffixKeys extends string = string,
  DomainKeys extends string = string,
  DomainListKeys extends string = string,
> {
  suffixes?: Record<SuffixKeys, unknown>;
  domains?: Record<DomainKeys, unknown>;
  domain_lists?: Record<DomainListKeys, unknown>;
}

export type ProcessedDomainsConfig<DomainKeys extends string, DomainListKeys extends string> = [
  Record<DomainKeys, string>,
  Record<DomainListKeys, string[]>,
];

// Updates to this logic should also be made in:
//   dropbox/server/compute_domains.py
//   go/src/dropbox/web/config/compute_domains.go
export function processDomains<S extends string, D extends string, DL extends string>(
  data: DomainsConfig<S, D, DL>,
  isProd: boolean,
  devServerHostname?: string
): ProcessedDomainsConfig<D, DL> {
  /**
   * Processes domains.public.yaml format.
   *
   * data is a map of labeled sections, which have mappings from
   * keys to values, and values in later sections can reference keys from
   * the 'suffixes' section
   *
   * Returns the 'domains' and 'domainLists' sections.
   */
  const context: ContextType = devServerHostname ? {hostname: devServerHostname} : {};
  if (data.suffixes) {
    const suffixes = processSection(data.suffixes, isProd, context);
    for (const [key, value] of Object.entries(suffixes)) {
      if (typeof value === 'string') {
        context[key] = value;
      } else {
        throw new Error('Lists in suffix section are not supported');
      }
    }
  }
  let domains;
  if (data.domains) {
    domains = processSection<D, string>(data.domains, isProd, context);
  } else {
    domains = {} as Record<D, string>;
  }
  let domainLists;
  if (data.domain_lists) {
    domainLists = processSection<DL, string[]>(data.domain_lists, isProd, context);
  } else {
    domainLists = {} as Record<DL, string[]>;
  }

  // check domains, domain lists types
  Object.entries(domains).forEach(([key, value]) => {
    if (typeof value !== 'string') {
      throw new Error(`Expected string in 'domains', found ${typeof value} for key ${key} instead`);
    }
  });
  Object.entries(domainLists).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      throw new Error(
        `Expected list in 'domainLists', found ${typeof value} for key ${key} instead`
      );
    }
  });
  return [domains, domainLists];
}

function processSection<K extends string, V extends string | string[]>(
  data: Record<K, unknown>,
  isProd: boolean,
  context: ContextType
): Record<K, V> {
  const result = {} as Record<K, V>;
  Object.entries(data).forEach(([key, value]) => {
    result[key as K] = processValue<V>(key, value, isProd, context) as V;
  });
  return result;
}

function processValue<V extends DomainConstant>(
  key: string,
  value: unknown,
  isProd: boolean,
  context: ContextType
): V {
  /**
   * Processes a value in domains.public.yaml.
   *
   * List of lists are flattened
   * $(suffix) references in string values are resolved
   * {dev: ..., prod: ...} are also resolved
   *
   * Returns a list of strings or a single string
   */
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    if ('dev' in value && 'prod' in value && Object.keys(value).sort().join(',') === 'dev,prod') {
      return processValue(key, isProd ? value.prod : value.dev, isProd, context);
    } else {
      throw new Error(`Dict value ${value} in key ${key} must have both dev and prod keys`);
    }
  } else if (Array.isArray(value)) {
    const seq: string[] = [];
    value.forEach((s) => {
      const processed = processValue(key, s, isProd, context);
      if (typeof processed === 'string') {
        seq.push(processed);
      } else {
        seq.push(...(processed as string[]));
      }
    });
    return seq as V;
  } else if (typeof value === 'string') {
    return processStringReferences(value, context) as V;
  }

  throw new Error(`Unknown value type ${typeof value} found within key ${key}`);
}

function processStringReferences(value: string, context: ContextType): string {
  /**
   * Processes $(key) references in value by looking them up
   * in the context.
   */
  return value.replace(/\$\(([^)]+)\)/g, (_, key) => context[key]);
}
